import { useState } from "react";
import { MessageCircleHeart, Loader2, Send, Trash2, ChevronDown, CornerDownRight, SmilePlus, X, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { maskEmail } from "../lib/mask";
import { tNL } from "../lib/journalI18n";

const COMMENT_EMOJIS = ["❤️", "👍", "😂", "😮", "😢", "🙏"];

const ReactionRow = ({ comment, reactions, myEmail, onReact, pickerFor, setPickerFor, t }) => {
  const grouped = COMMENT_EMOJIS.map((emoji) => ({ emoji, list: reactions.filter((r) => r.emoji === emoji) })).filter((g) => g.list.length > 0);
  if (!onReact && grouped.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={`comment-reactions-${comment.id}`}>
      {grouped.map(({ emoji, list }) => {
        const mine = myEmail && list.some((r) => r.author_email === myEmail || r.author_email === maskEmail(myEmail));
        return (
          <button key={emoji} data-testid={`comment-reaction-pill-${comment.id}-${emoji}`} disabled={!onReact}
            onClick={() => onReact?.(comment.id, emoji)} title={list.map((r) => r.author_name).join(", ")}
            className={`rounded-full px-2 py-0.5 text-xs leading-none flex items-center gap-1 border active:scale-90 transition-[background-color,border-color,transform] duration-200 ${
              mine ? "bg-[#B5838D]/15 border-[#B5838D]/50" : "bg-secondary/70 border-transparent"
            } ${onReact ? "cursor-pointer" : "cursor-default"}`}>
            <span>{emoji}</span><span className="font-mono font-semibold">{list.length}</span>
          </button>
        );
      })}
      {onReact && (
        <span className="relative">
          <button data-testid={`comment-react-btn-${comment.id}`} onClick={() => setPickerFor(pickerFor === comment.id ? null : comment.id)}
            className="p-1 rounded-full text-muted-foreground hover:bg-secondary transition-[background-color] duration-200" title={t("emojiTip")}>
            <SmilePlus className="w-3.5 h-3.5" />
          </button>
          {pickerFor === comment.id && (
            <span data-testid={`comment-emoji-picker-${comment.id}`} className="absolute bottom-full left-0 mb-1 z-20 flex gap-0.5 bg-card border border-border rounded-full shadow-lg px-1.5 py-1">
              {COMMENT_EMOJIS.map((emoji) => (
                <button key={emoji} data-testid={`comment-emoji-${comment.id}-${emoji}`}
                  onClick={() => { onReact(comment.id, emoji); setPickerFor(null); }}
                  className="text-base leading-none px-0.5 hover:scale-125 transition-transform duration-150">{emoji}</button>
              ))}
            </span>
          )}
        </span>
      )}
    </div>
  );
};

export const DayComments = ({ comments = [], reactions = [], idKey, onSubmit, onDelete, onReact, onMove, moveTargets = [], myEmail, disabledHint, placeholder = null, label = null, replyOnly = false, prominent = false, t = tNL, locale = "nl-BE" }) => {
  const [open, setOpen] = useState(comments.length > 0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);
  const [moveFor, setMoveFor] = useState(null);

  const roots = comments.filter((c) => !c.parent_id).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  const repliesOf = (id) => comments.filter((c) => c.parent_id === id).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit(text.trim(), replyTo?.id || null);
      setText("");
      setReplyTo(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (comments.length === 0 && (!onSubmit || replyOnly)) return null;

  const Bubble = ({ c, isReply }) => (
    <div data-testid={`comment-${c.id}`} className={`bg-[#B5838D]/5 border border-[#B5838D]/10 rounded-xl px-3.5 py-2.5 ${isReply ? "ml-7" : ""}`}>
      <p className="text-sm leading-relaxed">{c.text}</p>
      <ReactionRow comment={c} reactions={reactions.filter((r) => r.comment_id === c.id)} myEmail={myEmail} t={t}
        onReact={!disabledHint ? onReact : null} pickerFor={pickerFor} setPickerFor={setPickerFor} />
      <div className="flex items-center justify-between mt-1 gap-2">
        <p className="text-xs text-muted-foreground font-semibold min-w-0 truncate">
          {c.author_name}
          {c.is_owner && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5">{t("traveler")}</span>}
          <span className="font-mono font-normal"> · {new Date(c.created_at).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
        </p>
        <span className="flex items-center gap-0.5 shrink-0">
          {onMove && moveTargets.length > 0 && !c.parent_id && !c.entry_id && (
            <button data-testid={`move-comment-${c.id}`} onClick={() => setMoveFor(moveFor === c.id ? null : c.id)} title="Verplaats naar een reismoment"
              className="p-1 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
              <ArrowRightLeft className="w-3.5 h-3.5" />
            </button>
          )}
          {onSubmit && !disabledHint && (
            <button data-testid={`comment-reply-${c.id}`} onClick={() => setReplyTo(c.parent_id ? comments.find((x) => x.id === c.parent_id) || c : c)} title={t("replyTitle")}
              className="p-1 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
              <CornerDownRight className="w-3.5 h-3.5" />
            </button>
          )}
          {onDelete && (
            <button data-testid={`delete-comment-${c.id}`} onClick={() => onDelete(c.id)} title="Reactie verwijderen"
              className="p-1 rounded-full hover:bg-red-50 text-red-400 transition-[background-color] duration-200">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      </div>
      {moveFor === c.id && (
        <div className="mt-2 space-y-1" data-testid={`move-picker-${c.id}`}>
          <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Verplaats naar dit moment:</p>
          {moveTargets.map((t) => (
            <button key={t.id} data-testid={`move-target-${c.id}-${t.id}`}
              onClick={async () => { await onMove(c.id, t.id); setMoveFor(null); }}
              className="w-full text-left text-xs bg-secondary/60 hover:bg-secondary rounded-lg px-2.5 py-1.5 transition-[background-color] duration-200">
              {t.label}
            </button>
          ))}
          <button data-testid={`move-cancel-${c.id}`} onClick={() => setMoveFor(null)}
            className="text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-[color] duration-200">Annuleer</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-3" data-testid={`day-comments-${idKey}`}>
      {comments.length > 0 ? (
        <button data-testid={`comments-toggle-${idKey}`} onClick={() => setOpen((o) => !o)}
          className="text-[11px] font-semibold text-[#B5838D] hover:text-[#8E5D67] flex items-center gap-1.5 uppercase tracking-wide transition-[color] duration-200">
          <MessageCircleHeart className="w-3.5 h-3.5" /> {label || t("comments")} ({comments.length})
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      ) : prominent ? (
        !open && (
          <button data-testid={`comments-toggle-${idKey}`} onClick={() => setOpen(true)}
            className="w-full text-left bg-[#B5838D]/10 hover:bg-[#B5838D]/15 rounded-full px-4 py-2 text-sm text-muted-foreground transition-[background-color] duration-200">
            {placeholder || `${t("react")}...`}
          </button>
        )
      ) : (
        <button data-testid={`comments-toggle-${idKey}`} onClick={() => setOpen((o) => !o)}
          className="text-[11px] font-semibold text-muted-foreground hover:text-[#B5838D] flex items-center gap-1.5 uppercase tracking-wide transition-[color] duration-200">
          <MessageCircleHeart className="w-3.5 h-3.5" /> {t("react")}
        </button>
      )}
      {open && (
        <div className="space-y-2 mt-2">
          {roots.map((c) => (
            <div key={c.id} className="space-y-1.5">
              <Bubble c={c} />
              {repliesOf(c.id).map((r) => <Bubble key={r.id} c={r} isReply />)}
            </div>
          ))}
          {onSubmit && !(replyOnly && !replyTo) && (disabledHint ? (
            <p className="text-xs text-muted-foreground italic">{disabledHint}</p>
          ) : (
            <div>
              {replyTo && (
                <div data-testid={`reply-chip-${idKey}`} className="flex items-center gap-1.5 text-[11px] text-muted-foreground bg-secondary/70 rounded-full px-3 py-1 w-fit mb-1.5">
                  <CornerDownRight className="w-3 h-3" /> {t("replyTo")} <strong>{replyTo.author_name}</strong>
                  <button data-testid={`reply-cancel-${idKey}`} onClick={() => setReplyTo(null)} className="p-0.5 rounded-full hover:bg-secondary transition-[background-color] duration-200">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input data-testid={`comment-input-${idKey}`} value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()} maxLength={1000}
                  placeholder={replyTo ? `${t("replyTo")} ${replyTo.author_name}...` : (placeholder || `${t("react")}...`)}
                  className="flex-1 min-w-0 bg-[#B5838D]/10 rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#B5838D] transition-[box-shadow] duration-200" />
                <button data-testid={`comment-send-${idKey}`} onClick={send} disabled={!text.trim() || busy} title={t("sendComment")}
                  className="w-8 h-8 rounded-full bg-[#B5838D] hover:bg-[#8E5D67] text-white flex items-center justify-center disabled:opacity-40 shrink-0 active:scale-95 transition-[transform,background-color] duration-200">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
